/**
 * Resolves the files that a folder tagged to a roster position contributes to a
 * task. Nothing is written when a file is uploaded: the link is derived each
 * time the ticket is opened, so adding or removing files from the folder is
 * reflected immediately and there is nothing to backfill.
 */

export type PositionFolderTask = {
  assignee_kind?: string | null;
  assignee_id?: string | null;
  /** Wire field for user assignment; see `ActionFormValues.poc`. */
  poc?: string | null;
};

export type PositionFolderPosition = {
  id: string;
  name: string;
};

export type PositionFolderRosterEntry = {
  userId: string;
  incidentPositions: { positionName: string }[];
};

export type PositionFolderItem = {
  id: string;
  type: 'folder' | 'file';
  folder_name?: string | null;
  file_name?: string | null;
  notefolderId?: string | null;
  position_name?: string | null;
  position_template_slug?: string | null;
  mimetype?: string | null;
  updatedAt?: string;
};

export type PositionFile = {
  id: string;
  name: string;
  positionName: string;
  folderName: string;
};

const normalize = (name: string) => name.trim().toLowerCase();

/**
 * Seat names a task counts as belonging to: the seat it is assigned to, plus
 * every seat held by the person it is assigned to.
 */
export function positionNamesForTask(
  task: PositionFolderTask | null | undefined,
  positions: readonly PositionFolderPosition[],
  rosterEntries: readonly PositionFolderRosterEntry[]
): string[] {
  if (!task) return [];

  const names = new Set<string>();

  if (task.assignee_kind === 'position' && task.assignee_id) {
    const seat = positions.find((position) => position.id === task.assignee_id);
    if (seat) names.add(seat.name);
  }

  const userIds = new Set<string>();
  if (task.assignee_kind === 'user' && task.assignee_id) {
    userIds.add(task.assignee_id);
  }
  if (task.poc) userIds.add(task.poc);

  for (const entry of rosterEntries) {
    if (!userIds.has(entry.userId)) continue;
    for (const seat of entry.incidentPositions ?? []) {
      if (seat.positionName) names.add(seat.positionName);
    }
  }

  return Array.from(names);
}

/** Folder ids tagged to any of `positionNames`, plus all of their descendants. */
export function taggedFolderIds(
  items: readonly PositionFolderItem[],
  positionNames: readonly string[]
): Map<string, { positionName: string; folderName: string }> {
  const wanted = new Set(positionNames.map(normalize));
  const folders = items.filter((item) => item.type === 'folder');

  const matched = new Map<
    string,
    { positionName: string; folderName: string }
  >();
  for (const folder of folders) {
    const tag = folder.position_name;
    if (!tag || !wanted.has(normalize(tag))) continue;
    matched.set(folder.id, {
      positionName: tag,
      folderName: folder.folder_name ?? '',
    });
  }
  if (matched.size === 0) return matched;

  // Subfolders inherit the tag, so dropping a file one level down still lands
  // on the position. Iterating until nothing new matches handles any depth
  // without assuming the list is ordered parents-first.
  let grew = true;
  while (grew) {
    grew = false;
    for (const folder of folders) {
      if (matched.has(folder.id)) continue;
      const parentId = folder.notefolderId;
      if (!parentId) continue;
      const parent = matched.get(parentId);
      if (!parent) continue;
      matched.set(folder.id, {
        positionName: parent.positionName,
        folderName: folder.folder_name ?? parent.folderName,
      });
      grew = true;
    }
  }

  return matched;
}

/**
 * Every file living under a folder tagged to one of `positionNames`. Pass the
 * flat `useGetAllFiles` datasets for each scope you want covered; ids are
 * globally unique, so incident and org results can be concatenated.
 */
export function filesForPositions(
  items: readonly PositionFolderItem[],
  positionNames: readonly string[]
): PositionFile[] {
  if (positionNames.length === 0) return [];

  const folders = taggedFolderIds(items, positionNames);
  if (folders.size === 0) return [];

  const seen = new Set<string>();
  const files: PositionFile[] = [];

  for (const item of items) {
    if (item.type !== 'file') continue;
    if (!item.notefolderId) continue;
    const folder = folders.get(item.notefolderId);
    if (!folder) continue;
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    files.push({
      id: item.id,
      name: item.file_name ?? 'Untitled file',
      positionName: folder.positionName,
      folderName: folder.folderName,
    });
  }

  return files.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  );
}
